import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Calendar,
  Clock,
  Award,
  Play,
  CheckCircle,
  Share2,
  ArrowLeft,
  Star,
  Globe,
  Loader2,
  Copy,
  CalendarPlus,
  Facebook,
  Linkedin,
  Twitter,
  Mail,
  Monitor,
  Video,
  HardDrive,
  Building2,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Target,
  UserCheck,
  FileText,
  Hash,
  Signal,
  ShoppingCart,
  CalendarDays,
  Timer,
  Hourglass,
  Sparkles,
} from 'lucide-react';
import { seminarsAPI } from '@/services/api';
import { format } from 'date-fns';
import { formatET, getETAbbr } from '@/utils/dateUtils';
import { toast } from 'sonner';

// HR Industry names for CPE badge display
const HR_INDUSTRY_NAMES = ['HR', 'Human Resource', 'Human Resources', 'Cross Industry Functions'];

// Helper to check if seminar is HR industry (for CPE badge display)
const isHRIndustry = (industryName) => {
  if (!industryName) return false;
  return HR_INDUSTRY_NAMES.some(hr => 
    industryName.toLowerCase().includes(hr.toLowerCase())
  );
};

const levelColors = {
  basic: 'bg-green-100 text-green-700',
  intermediate: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
};

const levelLabels = {
  basic: 'Basic',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export default function SeminarDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [seminar, setSeminar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPricing, setSelectedPricing] = useState('live');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  
  // Manual accordion state - user clicks to expand/collapse
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchSeminar();
  }, [id]);

  const fetchSeminar = async () => {
    try {
      const response = await seminarsAPI.getById(id);
      setSeminar(response.data);
    } catch (error) {
      console.error('Error fetching seminar:', error);
      toast.error('Failed to load seminar details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/seminars/${id}` } });
      return;
    }

    const variantMap = {
      'live': 'Live Access for 1 Participant',
      'recorded': 'Download Access for 1 Participant',
      'digital': 'USB Flash Drive for 1 Participant',
      'group': 'Group Live (Up to 20 Participants)',
    };
    
    const variant = variantMap[selectedPricing] || 'Live Access for 1 Participant';
    
    setAddingToCart(true);
    const result = await addToCart('seminar', seminar.id, variant);
    setAddingToCart(false);
    
    if (result.success) {
      navigate('/cart');
    } else if (result.requiresAuth) {
      navigate('/login', { state: { from: `/seminars/${id}` } });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatET(dateStr, 'EEEE, MMMM dd, yyyy');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return formatET(dateStr, 'h:mm a');
  };

  const copySeminarCode = () => {
    if (seminar?.seminar_code) {
      navigator.clipboard.writeText(seminar.seminar_code);
      toast.success('Seminar code copied!');
    }
  };

  const addToGoogleCalendar = () => {
    if (!seminar?.scheduled_date) return;
    
    const startDate = new Date(seminar.scheduled_date);
    const endDate = seminar.end_date 
      ? new Date(seminar.end_date)
      : new Date(startDate.getTime() + seminar.duration_hours * 60 * 60000);
    
    const formatGoogleDate = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '');
    
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', seminar.title);
    url.searchParams.set('dates', `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`);
    url.searchParams.set('details', seminar.short_description || seminar.description?.slice(0, 200));
    url.searchParams.set('location', 'Online Seminar');
    
    window.open(url.toString(), '_blank');
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = seminar?.title || '';
    const text = seminar?.short_description || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`,
    };
    
    window.open(shareUrls[platform], '_blank');
    setShowShareMenu(false);
  };

  const getPricingOptions = () => {
    if (!seminar) return [];
    
    const options = [];
    
    if (seminar.price_live > 0 || seminar.price > 0) {
      options.push({
        id: 'live',
        name: 'Live Access',
        description: 'Join the live seminar session',
        price: seminar.price_live || seminar.price,
        icon: Monitor,
        features: ['Live participation', 'Q&A session', 'Certificate of completion', 'CPE credits'],
        participantInfo: 'For 1 Participant',
      });
    }
    
    if (seminar.price_recorded > 0) {
      options.push({
        id: 'recorded',
        name: 'Download Access',
        description: 'Watch at your convenience',
        price: seminar.price_recorded,
        icon: Video,
        features: ['On-demand access', 'Lifetime availability', 'Certificate of completion', 'CPE credits'],
        participantInfo: 'For 1 Participant',
      });
    }
    
    if (seminar.price_digital_download > 0) {
      options.push({
        id: 'digital',
        name: 'USB Flash Drive',
        description: 'Receive recording on USB drive',
        price: seminar.price_digital_download,
        icon: HardDrive,
        features: ['USB flash drive', 'Reference materials', 'Keep forever', 'CPE credits'],
        participantInfo: 'For 1 Participant',
      });
    }
    
    if (seminar.price_group_live > 0) {
      options.push({
        id: 'group',
        name: 'Group Live (Up to 20)',
        description: 'Corporate group access',
        price: seminar.price_group_live,
        icon: Building2,
        features: ['Up to 20 participants', 'Live participation', 'Bulk certificates', 'Corporate licensing'],
        participantInfo: 'Up to 20 Participants',
      });
    }
    
    if (options.length === 0) {
      options.push({
        id: 'live',
        name: 'Live Access',
        description: 'Join the live seminar session',
        price: seminar.price || 0,
        icon: Monitor,
        features: ['Live participation', 'Q&A session', 'Certificate of completion', 'CPE credits'],
        participantInfo: 'For 1 Participant',
      });
    }
    
    return options;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  if (!seminar) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="container-custom py-20 text-center">
          <h1 className="font-heading text-2xl text-navy-700 mb-4">Seminar not found</h1>
          <Link to="/seminars">
            <Button variant="outline" className="rounded-xl">Back to Seminars</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pricingOptions = getPricingOptions();
  const selectedOption = pricingOptions.find(p => p.id === selectedPricing) || pricingOptions[0];

  return (
    <div className="min-h-screen bg-transparent" data-testid="seminar-detail-page">
      {/* Hero Section - MailCraft grid canvas */}
      <section className="relative pt-28 pb-12 hero-canvas border-b border-zinc-200 overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb lastLabel={seminar.title} />
          {/* Two-column layout: Content (left) + AI Summary (right) */}
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column - Main Content (60%) */}
            <div className="lg:col-span-3">
              {/* Badges Row */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {seminar.seminar_code && (
                  <Badge 
                    className="bg-zinc-50 text-zinc-900 border border-zinc-200 cursor-pointer hover:bg-zinc-100"
                    onClick={copySeminarCode}
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    {seminar.seminar_code}
                    <Copy className="w-3 h-3 ml-2" />
                  </Badge>
                )}
                {seminar.seminar_type === 'live' && (
                  <Badge className="bg-purple-500 text-white">
                    <CalendarDays className="w-3 h-3 mr-1" />
                    {seminar.is_multi_day ? `${seminar.number_of_days}-Day Seminar` : 'Full-Day Seminar'}
                  </Badge>
                )}
                {seminar.seminar_type !== 'live' && (
                  <Badge className="bg-blue-500 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    Recorded
                  </Badge>
                )}
                {seminar.level && (
                  <Badge className={levelColors[seminar.level]}>
                    <Signal className="w-3 h-3 mr-1" />
                    {levelLabels[seminar.level]}
                  </Badge>
                )}
              {isHRIndustry(seminar.industry_name) && seminar.has_cpe_credits && seminar.cpe_credits > 0 && (
                <Badge className="bg-gold-500 text-navy-800">
                  <Award className="w-3 h-3 mr-1" />
                  {seminar.cpe_credits} CPE Credits
                </Badge>
              )}
              {(seminar.sub_industry_name || seminar.industry_name) && (
                <Badge variant="outline" className="border-zinc-300 text-zinc-900">
                  {seminar.sub_industry_name || seminar.industry_name}
                </Badge>
              )}
            </div>

            {/* Title - Full width */}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 mb-4">
              {seminar.title}
            </h1>
            <p className="text-zinc-600 text-lg mb-6 max-w-3xl">
              {seminar.short_description || seminar.description?.slice(0, 200)}
            </p>

            {/* Meta Info - Date, Time, Duration */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                <Calendar className="w-5 h-5 text-navy-600" />
                <span className="text-zinc-700">{formatDate(seminar.scheduled_date)}</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                <Timer className="w-5 h-5 text-navy-600" />
                <span className="text-zinc-700">{formatTime(seminar.scheduled_date)} {getETAbbr(seminar.scheduled_date)}</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-50 rounded-xl px-4 py-2">
                <Hourglass className="w-5 h-5 text-navy-600" />
                <span className="text-zinc-700">{seminar.duration_hours} hours</span>
              </div>
            </div>
            
            {/* Actions Row - Add to Calendar & Share */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Button 
                variant="outline" 
                size="sm"
                className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                onClick={addToGoogleCalendar}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Add to Calendar
              </Button>
              <div className="relative">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-zinc-300 text-zinc-900 hover:bg-zinc-50 rounded-xl"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                {showShareMenu && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl p-2 z-50 min-w-48">
                    <button 
                      onClick={() => shareOnSocial('facebook')}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook
                    </button>
                    <button 
                      onClick={() => shareOnSocial('linkedin')}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                    >
                      <Linkedin className="w-4 h-4 text-blue-700" />
                      LinkedIn
                    </button>
                    <button 
                      onClick={() => shareOnSocial('twitter')}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                    >
                      <Twitter className="w-4 h-4 text-sky-500" />
                      Twitter
                    </button>
                    <button 
                      onClick={() => shareOnSocial('email')}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 rounded-lg text-slate-700"
                    >
                      <Mail className="w-4 h-4 text-slate-600" />
                      Email
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Speakers */}
            {seminar.speakers?.length > 0 && (
              <div className="pt-6 border-t border-zinc-200">
                <h3 className="text-zinc-900 font-medium mb-4">Presented by</h3>
                <div className="flex flex-wrap gap-4">
                  {seminar.speakers.map((speaker) => (
                    <Link
                      key={speaker.id}
                      to={`/speakers/${speaker.id}`}
                      className="flex items-center gap-3 bg-zinc-50 rounded-xl p-4 hover:bg-zinc-100 transition-colors"
                    >
                      <img
                        src={speaker.profile_image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100'}
                        alt={speaker.first_name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-gold-400"
                      />
                      <div>
                        <p className="text-zinc-900 font-semibold">
                          {speaker.first_name} {speaker.last_name}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {speaker.expertise_areas?.slice(0, 2).join(' • ')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

            {/* Right Column - AI Summary (40%) */}
            <div className="lg:col-span-2">
              {(seminar.ai_summary?.overview || seminar.ai_summary?.takeaways?.length > 0) && (
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-navy-600" />
                    </div>
                    <h3 className="text-zinc-900 font-semibold">Quick Summary</h3>
                  </div>
                  
                  {seminar.ai_summary?.overview && (
                    <p className="text-zinc-600 text-sm leading-relaxed mb-4">
                      {seminar.ai_summary.overview}
                    </p>
                  )}
                  
                  {seminar.ai_summary?.takeaways?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-zinc-900 text-sm font-medium">Key Takeaways:</h4>
                      <ul className="space-y-2">
                        {seminar.ai_summary.takeaways.map((takeaway, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                            <CheckCircle className="w-4 h-4 text-navy-600 mt-0.5 flex-shrink-0" />
                            <span>{takeaway}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Placeholder when no summary */}
              {!seminar.ai_summary?.overview && !seminar.ai_summary?.takeaways?.length && (
                <div className="hidden lg:block bg-zinc-50 rounded-2xl p-6 border border-zinc-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-slate-400" />
                    </div>
                    <h3 className="text-slate-400 font-medium">Quick Summary</h3>
                  </div>
                  <p className="text-slate-500 text-sm italic">
                    AI-generated summary coming soon...
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Separate from hero */}
      <section className="py-8 bg-slate-50">
        <div className="container-custom">
          <div className="max-w-md mx-auto">
            <Card className="overflow-hidden border-0 shadow-xl rounded-2xl">
              <CardContent className="p-6">
                {/* Pricing Options */}
                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-navy-700 mb-3">Select Access Type</h4>
                  {pricingOptions.map((option) => (
                    <div
                      key={option.id}
                      onClick={() => setSelectedPricing(option.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedPricing === option.id
                          ? 'border-gold-500 bg-gold-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedPricing === option.id ? 'bg-gold-500 text-navy-800' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <option.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-navy-700">{option.name}</p>
                            <p className="text-xs text-slate-500">{option.description}</p>
                            {option.participantInfo && (
                              <p className="text-xs text-gold-600 font-medium mt-0.5">{option.participantInfo}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-navy-700">${option.price}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  className="w-full h-14 bg-zinc-950 hover:bg-zinc-800 text-white font-bold text-lg rounded-xl"
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  data-testid="add-to-cart-btn"
                >
                  {addingToCart ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Adding to Cart...
                    </>
                  ) : selectedOption?.price > 0 ? (
                    <>
                      <ShoppingCart className="mr-2 h-5 w-5" />
                      Add to Cart - ${selectedOption.price}
                    </>
                  ) : (
                    'Register Free'
                  )}
                </Button>

                {/* Features */}
                {selectedOption && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="space-y-2">
                      {selectedOption.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Content Accordion */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Unified Accordion for all devices */}
              <Card className="border-slate-200 rounded-2xl overflow-hidden">
                <Accordion 
                  type="single" 
                  collapsible 
                  value={activeSection}
                  onValueChange={setActiveSection}
                  className="w-full"
                >
                  {/* Overview Section */}
                  <AccordionItem value="overview" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-navy-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-navy-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Seminar Overview
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px] prose prose-slate max-w-none">
                        <p className="text-slate-600 leading-relaxed whitespace-pre-line text-sm md:text-base">
                          {seminar.overview || seminar.description}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Areas Covered Section */}
                  <AccordionItem value="areas" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Target className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Areas Covered
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px]">
                        {seminar.areas_covered?.length > 0 ? (
                          <ul className="space-y-3">
                            {seminar.areas_covered.map((area, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                <span className="text-slate-600 text-sm md:text-base">{area}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {seminar.tags?.map((tag, idx) => (
                              <Badge key={idx} className="bg-slate-100 text-slate-700 px-4 py-2">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Who Should Attend Section */}
                  <AccordionItem value="who" className="border-b border-slate-200">
                    <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                        </div>
                        <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                          Who Should Attend?
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="md:ml-[52px] prose prose-slate max-w-none">
                        <p className="text-slate-600 leading-relaxed whitespace-pre-line text-sm md:text-base">
                          {seminar.who_should_attend || seminar.target_audience ||
                            "This seminar is designed for compliance professionals, risk managers, auditors, legal counsel, and executives who want comprehensive training on industry best practices."}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Learning Objectives Section */}
                  {seminar.learning_objectives?.length > 0 && (
                    <AccordionItem value="objectives" className="border-0">
                      <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                            <Target className="w-4 h-4 md:w-5 md:h-5 text-gold-600" />
                          </div>
                          <span className="font-heading text-base md:text-lg font-semibold text-navy-700 text-left">
                            Learning Objectives
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                        <div className="md:ml-[52px]">
                          {(() => {
                            const objectives = seminar.learning_objectives;
                            const joined = Array.isArray(objectives) ? objectives.join('') : '';
                            if (joined.includes('<')) {
                              let counter = 0;
                              const styledContent = joined
                                .replace(/<ul>/g, '<ol class="space-y-3 list-none">')
                                .replace(/<\/ul>/g, '</ol>')
                                .replace(/<li>/g, () => {
                                  counter++;
                                  return `<li class="flex items-start gap-3"><span class="w-6 h-6 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">${counter}</span><span class="text-slate-600">`;
                                })
                                .replace(/<\/li>/g, '</span></li>');
                              return (
                                <div 
                                  className="prose prose-slate max-w-none text-sm md:text-base"
                                  dangerouslySetInnerHTML={{ __html: styledContent }}
                                />
                              );
                            }
                            return (
                              <ol className="space-y-3">
                                {seminar.learning_objectives.map((objective, idx) => (
                                  <li key={idx} className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                      {idx + 1}
                                    </span>
                                    <span className="text-slate-600 text-sm md:text-base">{objective}</span>
                                  </li>
                                ))}
                              </ol>
                            );
                          })()}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                </Accordion>
              </Card>

              {/* Speakers Section */}
              {seminar.speakers?.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-heading text-2xl font-bold text-navy-700 mb-6">
                    About Your Instructor{seminar.speakers.length > 1 ? 's' : ''}
                  </h2>
                  <div className="space-y-6">
                    {seminar.speakers.map((speaker) => (
                      <Card key={speaker.id} className="border-slate-200 rounded-2xl overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-48 h-48 md:h-auto relative">
                              <img
                                src={speaker.profile_image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'}
                                alt={speaker.first_name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 p-6">
                              <Link to={`/speakers/${speaker.id}`}>
                                <h3 className="font-heading text-xl font-bold text-navy-700 mb-1 hover:text-gold-600 transition-colors">
                                  {speaker.first_name} {speaker.last_name}
                                </h3>
                              </Link>
                              <p className="text-gold-600 font-medium mb-3">
                                {speaker.expertise_areas?.join(' • ')}
                              </p>
                              <p className="text-slate-600 mb-4 line-clamp-3">
                                {speaker.bio || speaker.headline}
                              </p>
                              <Link 
                                to={`/speakers/${speaker.id}`}
                                className="text-navy-600 hover:text-gold-600 font-medium flex items-center gap-1"
                              >
                                View Profile
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Info Card */}
                <Card className="border-slate-200 rounded-2xl">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {seminar.seminar_code && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Seminar Code</span>
                          <span className="font-mono font-medium text-navy-700">{seminar.seminar_code}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-medium text-navy-700">{seminar.duration_hours} hours</span>
                      </div>
                      {seminar.is_multi_day && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Number of Days</span>
                          <span className="font-medium text-navy-700">{seminar.number_of_days} days</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Level</span>
                        <Badge className={levelColors[seminar.level || 'intermediate']}>
                          {levelLabels[seminar.level || 'intermediate']}
                        </Badge>
                      </div>
                      {isHRIndustry(seminar.industry_name) && seminar.has_cpe_credits && seminar.cpe_credits > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">CPE Credits</span>
                          <span className="font-medium text-navy-700">{seminar.cpe_credits}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Certificate</span>
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Included
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
